import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function AdminInviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/admin-invite/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(e.response?.data?.detail || "Invite not found."));
  }, [token]);

  const accept = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = { token };
      if (!info.account_exists) {
        if (!name.trim() || password.length < 8) {
          toast.error("Name and password (min 8 chars) required.");
          setBusy(false);
          return;
        }
        payload.name = name.trim();
        payload.password = password;
      }
      await api.post("/auth/admin-invite/accept", payload);
      toast.success("Welcome aboard — you're now an admin.");
      if (refresh) await refresh();
      nav("/admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't accept invite.");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="invite-error">
        <AlertTriangle className="h-10 w-10 mx-auto text-red-400 mb-4" />
        <h1 className="font-display text-3xl font-bold">Invite unavailable</h1>
        <p className="text-white/60 mt-2">{error}</p>
        <Link to="/" className="ember-btn mt-6 inline-flex">Back to home</Link>
      </div>
    );
  }
  if (!info) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-[#E65F2B] border-t-transparent animate-spin" /></div>;
  }

  // If signed in as a different email than the invite — block.
  if (user && user.email && user.email.toLowerCase() !== info.email.toLowerCase()) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="invite-wrong-account">
        <AlertTriangle className="h-10 w-10 mx-auto text-yellow-400 mb-4" />
        <h1 className="font-display text-3xl font-bold">Sign in as {info.email}</h1>
        <p className="text-white/60 mt-2">
          This invite is for <b>{info.email}</b> but you're signed in as <b>{user.email}</b>. Sign out and try again from the email link.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-6 py-16" data-testid="admin-invite-page">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="h-5 w-5 text-[#E65F2B]" />
        <p className="text-eyebrow text-[#E65F2B]">Admin invitation</p>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
        You've been invited to <span className="text-[#E65F2B]">administer</span> Christoreel.
      </h1>
      <p className="text-white/60 mt-4">
        Invitation for <b className="text-white break-all">{info.email}</b> from <b className="text-white">{info.invited_by_email}</b>.
      </p>
      {info.message && (
        <div className="mt-4 bg-white/5 rounded-lg p-4 border-l-2 border-[#E65F2B]/40">
          <p className="text-xs text-[#E65F2B] mb-1">Personal note</p>
          <p className="text-sm whitespace-pre-wrap">{info.message}</p>
        </div>
      )}

      <form onSubmit={accept} className="card-surface p-6 mt-8 space-y-4" data-testid="invite-form">
        {info.account_exists ? (
          <p className="text-sm text-white/70">
            You already have a Christoreel account. Clicking Accept will upgrade it to admin and sign you in.
          </p>
        ) : (
          <>
            <p className="text-sm text-white/70">
              You don't have an account yet — pick a name and password to create one. It'll be created with admin role.
            </p>
            <div>
              <label className="text-eyebrow mb-2 block">Your name</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                className="input-dark"
                placeholder="Esther Adebayo"
                disabled={busy}
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <label className="text-eyebrow mb-2 block">Choose a password (min 8 chars)</label>
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                maxLength={200}
                className="input-dark"
                disabled={busy}
                data-testid="invite-password-input"
              />
            </div>
          </>
        )}
        <button
          type="submit"
          disabled={busy || (!info.account_exists && (!name.trim() || password.length < 8))}
          className="ember-btn w-full disabled:opacity-40"
          data-testid="invite-accept-button"
        >
          <CheckCircle2 className="h-4 w-4" />
          {busy ? "Working..." : info.account_exists ? "Accept & become admin" : "Create admin account"}
        </button>
      </form>

      <p className="text-xs text-white/30 mt-4 text-center">
        Invite expires {info.expires_at ? new Date(info.expires_at).toLocaleDateString() : "soon"}.
      </p>
    </div>
  );
}
